/**
 * Client card — used in both board columns and list/grid views.
 * Shows name, vertical, plan, MRR, health badge, and next touchpoint.
 * Internal clients show an "Internal" chip and are pinned to the top (LXP-47).
 */
import { Link } from 'react-router'
import { Building2, Calendar, Building } from 'lucide-react'
import type { Client } from '../../lib/api'
import { HealthBadge } from './HealthBadge'

interface ClientCardProps {
  client: Client
  variant?: 'grid' | 'board' | 'row'
  /** When false, MRR is hidden. Defaults to true. */
  showFinancials?: boolean
}

export function ClientCard({ client, variant = 'grid', showFinancials = true }: ClientCardProps) {
  const initials = client.name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase()

  if (variant === 'row') {
    return (
      <Link to={`/clients/${client.id}`} className="client-row-link">
        <div className="client-row">
          <div className="client-row__left">
            <span className="client-avatar client-avatar--sm">{initials}</span>
            <div className="client-row__info">
              <span className="client-row__name">{client.name}</span>
              {client.vertical && (
                <span className="client-row__vertical">{client.vertical}</span>
              )}
              {client.kind === 'internal' && (
                <span className="client-row__kind">
                  <Building size={12} /> Internal
                </span>
              )}
            </div>
          </div>
          <span className="client-row__plan">{client.plan ?? '—'}</span>
          {showFinancials && (
            <span className="client-row__mrr mono">
              {client.mrr ? `$${client.mrr.toLocaleString()}/mo` : '—'}
            </span>
          )}
          <HealthBadge health={client.health} size="sm" />
        </div>
      </Link>
    )
  }

  // grid & board variants share the same card shape
  return (
    <Link to={`/clients/${client.id}`} className="client-card-link">
      <div className={`client-card-v2${variant === 'board' ? ' client-card-v2--board' : ''}`}>
        <div className="client-card-v2__top">
          <span className="client-avatar">{initials}</span>
          <div className="client-card-v2__identity">
            <h3 className="client-card-v2__name">{client.name}</h3>
            {client.vertical && (
              <span className="client-card-v2__vertical">
                <Building2 size={12} /> {client.vertical}
              </span>
            )}
          </div>
          <HealthBadge health={client.health} size="sm" />
        </div>

        <div className="client-card-v2__meta">
          {client.kind === 'internal' && (
            <span className="client-card-v2__chip client-card-v2__chip--internal">
              <Building size={12} /> Internal
            </span>
          )}
          {client.plan && <span className="client-card-v2__chip">{client.plan}</span>}
          {showFinancials && client.mrr != null && client.mrr > 0 && (
            <span className="client-card-v2__mrr mono">${client.mrr.toLocaleString()}/mo</span>
          )}
        </div>

        {client.notes && (
          <div className="client-card-v2__next">
            <Calendar size={12} />
            <span>{client.notes.length > 60 ? client.notes.slice(0, 60) + '…' : client.notes}</span>
          </div>
        )}
      </div>
    </Link>
  )
}
